import { CanActivateFn, RedirectCommand, Routes, Router } from '@angular/router';
import { inject } from '@angular/core';
import { resolveBackend } from './inbound-component/backend-resolver';
import { DisplayViewComponent } from './display-view-component/display-view-component';
import { InboundComponent } from './inbound-component/inbound-component';
import { environment } from '../environments/environment';
import { ApiPartnerEnum } from '@claas-rdv/rdv-client';
import { LANGUAGE_QUERY_PARAM } from './services/language.service';

/**
 * Guard that only allows access in non-production environments.
 * Used to protect dev-only routes like /dev/test.
 */
const devOnlyGuard: CanActivateFn = () => {
  if (environment.production) {
    console.warn('Dev routes are not available in production');
    return false;
  }
  return true;
};

/**
 * Guard for DisplayViewComponent routes.
 * If no `jwt` query param is present, redirect to the appropriate SSO entry
 * point based on the `apiPartner` query param:
 *   - SOFF → /security/okta/login        (forwards: machine, autoConnect, lang)
 *   - CC3  → /security/auth0/login       (forwards: machine, autoConnect, lang,
 *                                          session_transfer_token, organization)
 *   - (none / unknown) → show an error page by navigating to /error/no-token
 */
const requireJwtGuard: CanActivateFn = (route) => {
  const router = inject(Router);

  if (route.queryParamMap.has('jwt')) {
    return true; // JWT present — proceed normally
  }

  // Forward machine, autoConnect, interclient_token and lang to the SSO entry point
  const machine = route.queryParamMap.get('machine') ?? undefined;
  const autoConnect = route.queryParamMap.get('autoConnect') ?? undefined;
  const interclientToken = route.queryParamMap.get('interclient_token') ?? undefined;
  const lang = route.queryParamMap.get(LANGUAGE_QUERY_PARAM) ?? undefined;
  const queryParams: Record<string, string> = {};
  if (machine)           queryParams['machine'] = machine;
  if (autoConnect)       queryParams['autoConnect'] = autoConnect;
  if (interclientToken)  queryParams['interclient_token'] = interclientToken;
  if (lang)              queryParams[LANGUAGE_QUERY_PARAM] = lang;

  const apiPartner = route.queryParamMap.get('apiPartner');
  if (apiPartner === ApiPartnerEnum.SOFF) {
    return new RedirectCommand(router.createUrlTree(['/security/okta/login'], { queryParams }));
  }
  if (apiPartner === ApiPartnerEnum.CC3) {
    // Also forward Auth0-specific params so InboundComponent can use them
    const sessionTransferToken = route.queryParamMap.get('session_transfer_token') ?? undefined;
    const organization = route.queryParamMap.get('organization') ?? undefined;
    if (sessionTransferToken) queryParams['session_transfer_token'] = sessionTransferToken;
    if (organization)         queryParams['organization'] = organization;
    return new RedirectCommand(router.createUrlTree(['/security/auth0/login'], { queryParams }));
  }

  // No apiPartner or unrecognized value — show error
  return new RedirectCommand(router.createUrlTree(['/error/no-token']));
};

export const routes: Routes = [
  // Dev-only test page (protected by guard, only accessible when production === false)
  {
    path: 'dev/test',
    loadComponent: () =>
      import('./dev-test-component/dev-test-component').then(
        (m) => m.DevTestComponent
      ),
    canActivate: [devOnlyGuard],
  },
  // Dev-only raw Guacamole client (production === false only)
  {
    path: 'dev/guacamole',
    loadComponent: () =>
      import('./dev-guacamole-component/dev-guacamole-component').then(
        (m) => m.DevGuacamoleComponent
      ),
    canActivate: [devOnlyGuard],
  },
  // Okta SSO entry point (Händler / Dealer)
  // Also serves as the Okta redirect callback URI — InboundComponent handles both:
  // - Fresh entry with ?sessionToken=... → exchanges for Okta session
  // - Callback with ?code=&state=... → exchanges authorization code for tokens
  // - Return visit (no special params) → checks existing authentication
  {
    path: 'security/okta/login',
    component: InboundComponent,
    resolve: {
      resolvedPartnerSystem: resolveBackend,
    },
  },
  // Auth0 SSO entry point (Kunden / Customer)
  {
    path: 'security/auth0/login',
    component: InboundComponent,
    resolve: {
      resolvedPartnerSystem: resolveBackend,
    },
  },
  // Display view — target after successful authentication
  {
    path: 'rdv',
    component: DisplayViewComponent,
    canActivate: [requireJwtGuard],
  },
  // Error: no token and no recognisable apiPartner
  {
    path: 'error/no-token',
    component: DisplayViewComponent, // renders "No access token provided" naturally
  },
  // Fallback: catch-all — also guarded so direct / access redirects to SSO
  {
    path: '**',
    component: DisplayViewComponent,
    canActivate: [requireJwtGuard],
  },
];
